import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PlexEnvironment } from '@app/env';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

@Injectable()
export class ItemGridService extends BehaviorSubject<GridDataResult> {
  private http = inject(HttpClient);
  public environment = inject(PlexEnvironment);

  private BASE_URL = this.environment.apiUrl;
  private tableName: string = 'itemccct';
  public loading: boolean = false;

  public state: State = {
    skip: 0,
    take: 5
  };

  constructor() {
    super(null);
  }

  private toCustomEndpointString(state: GridState): string {
    let filterString: string = '';
    let pageNumber: number = state.pageNumber;
    let pageSize: number = state.pageSize;
    let projectTypeId = state.projectTypeId;
    if (state.filter && state.filter.filters.length > 0) {
      state.filter.filters.forEach(x => {
        const filterValue = `${x.value}`;
        const encodedFilterValue = encodeURIComponent(filterValue);

        if (x.field === 'GFCM') {
          const isNumeric = /^\d+$/.test(filterValue);
          const field = isNumeric ? `costCodeValue` : `costCodeDescription`;
          filterString += `&${field}=${encodedFilterValue}`;
          return;
        }

        filterString += `&${x.field}=${encodedFilterValue}`;
      });
    }
    let sortString: string = '';
    if (state.sort?.length) {
      sortString += `&sortProperty=${state.sort[0].field}&sortDirection=${state.sort[0].dir}`;
    }

    return `?pageSize=${pageSize}&pageNumber=${pageNumber}&projectTypeId=${projectTypeId}${filterString}${sortString}`;
  }

  private fetch(tableName: string, state: GridState): Observable<GridDataResult> {
    this.loading = true;
    let queryStr = this.toCustomEndpointString(state);

    return this.http
      .get(`${this.BASE_URL}${tableName}${queryStr}`).pipe(
        map(response => response),
        map((response: any) => {
          return <GridDataResult>{
            data: response.CostCodeCostTypes,
            total: parseInt(response.TotalCount, 10)
          };
        }),
        finalize(() => this.loading = false)
      );
  }

  // For Project Resource Items
  public query(state: GridState): void {
    this.fetch(this.tableName, state)
      .subscribe(x => super.next(x));
  }
}

export class GridState {
  pageSize?: number;
  pageNumber?: number;
  projectTypeId?: number;
  sort?: Array<GridStateSort>;
  filter?: GridStateFilter;

  constructor(pageSize: number = 50, pageNumber: number = 1, projectTypeId: number = null, sort: Array<GridStateSort> = []) {
    this.pageSize = pageSize;
    this.pageNumber = pageNumber;
    this.projectTypeId = projectTypeId;
    this.sort = sort;
  }
}

export class GridStateSort {
  field: string;
  dir?: 'asc' | 'desc';

  constructor(field: string, dir: 'asc' | 'desc' = null) {
    this.field = field;
    this.dir = dir;
  }
}

export class GridStateFilter {
  filters: Array<GridStateFilterItem>;
}

export class GridStateFilterItem {
  field: string;
  value: any;
}